import { useEffect } from "react";

type UseCloseWhenOutsideViewportProps<TTarget extends Element> = {
  isOpen: boolean;
  targetRef: React.RefObject<TTarget | null>;
  onClose: () => void;
  tolerance?: number;
};

/**
 * Calls onClose when the target element is outside the viewport.
 */
export function useCloseWhenOutsideViewport<TTarget extends Element>({
  isOpen,
  targetRef,
  onClose,
  tolerance = 1,
}: UseCloseWhenOutsideViewportProps<TTarget>) {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const checkIsOutside = () => {
      const targetElement = targetRef.current;

      if (!targetElement) {
        return;
      }

      const targetRect = targetElement.getBoundingClientRect();

      const isOutside =
        targetRect.bottom < 0 - tolerance ||
        targetRect.top > window.innerHeight + tolerance ||
        targetRect.right < 0 - tolerance ||
        targetRect.left > window.innerWidth + tolerance;

      if (isOutside) {
        onClose();
      }
    };

    const animationFrameId = requestAnimationFrame(checkIsOutside);

    window.addEventListener("scroll", checkIsOutside, true);
    window.addEventListener("resize", checkIsOutside);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("scroll", checkIsOutside, true);
      window.removeEventListener("resize", checkIsOutside);
    };
  }, [isOpen, targetRef, onClose, tolerance]);
}
